import React, { PureComponent } from 'react';
import * as d3 from 'd3';
import './App.css';

const margin = { top: 20, right: 50, bottom: 20, left: 50 };

const clusterLabels = {
  '420': 'Alvarado',
  '478': 'Clarendon',
  '479': 'Lilienthal',
  '485': 'Yu',
  '497': 'Webster',
  '539': 'Feinstein',
  '569': 'Peabody',
  '589': 'Grattan',
  '618': 'Buena Vista',
  '664': 'Lafayette',
  '676': 'Lawton',
  '718': 'McKinley',
  '722': 'Miraloma',
  '735': 'New Traditions',
  '746': 'Ortega',
  '750': 'Sunset',
  '796': 'Rooftop',
  '823': 'Sherman',
  '842': 'Sunnyside',
  '876': 'West Portal',
};

const minExampleLength = 3;
const maxExampleLength = 4;

class ApplicationsVisualization extends PureComponent {

  state = {
    data: [],
    examples: []
  }

  setDimensions() {
    let drawWidth = this.props.drawWidth, drawHeight = this.props.drawHeight;
    let data = [];
    // Precompute both the pack and force layout positions, which is simpler than trying to compute them lazily and store in state.
    // State gets tricky with objects because you might end up mutating them.
    // Do everything in one function because drawWidth and drawHeight would not yet be set if we put them into state.
    if (this.props.applications.length > 0) {
      //console.log('computing positions');
      //console.log(drawWidth);
      const pack = d3.pack()
        .size([drawWidth, drawHeight]) // pack sets x, y, and r to suit these dimensions
        .padding(1.5);
      const root = d3.hierarchy({ children: this.props.applications })
        .sum(d => d.applications);
      data = pack(root).leaves(); // we will keep the data structure used by pack
      data.forEach(d => {
        // Stash the x and y coordinates set by pack, because the force layout will clobber them.
        d.packX = d.x;
        d.packY = d.y;
      });
      const filteredData = data.filter(d => this.props.browsableIDs.has(d.data.id));
      const xScale = d3.scaleLinear()
        .domain(d3.extent(filteredData, d => d.data.tsne.x))
        .range([0, drawWidth]);
      const yScale = d3.scaleLinear()
        .domain(d3.extent(filteredData, d => d.data.tsne.y))
        .range([0, drawHeight]);
      // Use a force layout to ensure that the circles do not overlap. The x and y override whatever was set by pack.
      const simulation = d3.forceSimulation(filteredData)
        .force('x', d3.forceX(d => xScale(d.data.tsne.x)))
        .force('y', d3.forceY(d => yScale(d.data.tsne.y)))
        .force('collide', d3.forceCollide(d => d.r + 1)) // use existing radius set by pack
        .stop();
      for (let i = 0; i < 120; ++i) simulation.tick();
      filteredData.forEach(d => {
        d.tsneX = d.x;
        d.tsneY = d.y;
      });
    }
    this.setState({ data });
  }

  componentDidMount() {
    this.setDimensions();
    this.update();
  }

  componentDidUpdate(prevProps) {
    //console.log('prev step:', prevProps.step);
    //console.log('current step:', this.props.step);
    if (prevProps.drawWidth !== this.props.drawWidth || prevProps.drawHeight !== this.props.drawHeight || prevProps.applications !== this.props.applications) {
      this.setDimensions();
    }
    if (this.state.data.length > 0 && this.state.examples.length === 0) {
      let examples = [];
      const candidateExamples = this.state.data.filter(d => d.data.schools.length >= minExampleLength && d.data.schools.length <= maxExampleLength && new Set(d.data.schools).size === d.data.schools.length);
      examples = d3.merge([
        candidateExamples.filter(d => d.data.assignedRank === 1).slice(0, 2),
        candidateExamples.filter(d => d.data.assignedRank !== 1).slice(0, 2)
      ]);
      this.setState({ examples });
    }
    this.update();
  }

  shouldHighlight = (d) => {
    const filters = [
      this.props.selectedAssignmentStatus,
      this.props.selectedEnrollmentStatus,
      this.props.selectedCTIPStatus,
      this.props.selectedZip,
      this.props.selectedSchool
    ];
    if (filters.every(d => d === null)) return false;
    const datum = [d.assigned1stChoice, d.enrolledStatus, d.ctip, d.zip, d.schools[0]];
    return filters.every((d, i) => { return d === null || d.value === datum[i] });
  }

  showIntro = () => {

    const step = this.props.step;
    const examples = this.state.examples;
    const g = d3.select(this.chartArea);
    const miniAppWidth = 125;
    const miniAppPadding = 15;

    const miniApp = g.selectAll(".mini-application")
      .data(examples, d => d.data.id);

    const miniAppG = miniApp.enter().append("g")
      .attr("class", "mini-application")
      .attr("transform", (d, i) => { return "translate(" + (Math.floor(i / 2) ? this.props.drawWidth / 2 + miniAppPadding: this.props.drawWidth / 2 - miniAppWidth - miniAppPadding) + "," + (margin.top + (i % 2) * 150) + ")"; });

    miniAppG.append("rect")
      .attr("width", 125)
      .attr("height", d => d.data.applications * 18 + 45);

    miniAppG.append("text")
      .attr("x", 15)
      .attr("y", 20)
      .attr("dy", "0.35em")
      .text(d => "Application #" + d.data.id);

    miniAppG.selectAll(".mini-application-school")
      .data(d => d.data.schools)
      .enter().append("text")
      .attr("class", "mini-application-school")
      .text((d, i) => { return (i + 1) + ". " + this.props.schoolCodes.get(d).name; })
      .attr("x", 15)
      .attr("y", (d, i) => { return 45 + i * 18; })
      .attr("dy", "0.35em")
      .style("fill", "#000")
      .style("opacity", 0);

    if (step <= this.props.stepsEnum.SHOW_ASSIGNED_SCHOOLS) {

      const group = g.selectAll(".mini-application")
        .transition().duration(1000)
        .attr("transform", (d, i) => { return "translate(" + (Math.floor(i / 2) ? this.props.drawWidth / 2 + miniAppPadding: this.props.drawWidth / 2 - miniAppWidth - miniAppPadding) + "," + (margin.top + (i % 2) * 150) + ")"; });

      group.select("rect")
        .attr("width", miniAppWidth)
        .attr("height", d => d.data.applications * 18 + 45)
        .attr("stroke", "#fff")
        .attr("fill", "#f2f2f2")
        .attr("rx", 5)
        .attr("ry", 5);

      group.selectAll("text")
        .style("opacity", 1);

      group.style("opacity", null);

      if (step === this.props.stepsEnum.SHOW_ASSIGNED_SCHOOLS) {
        group.each(function(d) { // use regular function, not arrow function, to keep d3's "this" context
          d3.select(this).selectAll('.mini-application-school').style("fill", s => s === d.data.assigned ? "#000" : "#bbb");
        });
      } else {
        group.selectAll("text")
          .style("fill", "#000");
      }
    } else if (step === this.props.stepsEnum.TRANSITION_APPLICATIONS_TO_CIRCLES) {

      const group = g.selectAll(".mini-application")
        .transition().duration(1000)
        .attr("transform", (d, i) => { return "translate(" + (Math.floor(i / 2) ? this.props.drawWidth / 2 + miniAppPadding: this.props.drawWidth / 2 - miniAppWidth - miniAppPadding) + "," + (margin.top + (i % 2) * 150) + ")"; });

      group.select("rect")
        .attr("stroke", "#fff")
        .attr("fill", "var(--base-data-color)")
        .attr("width", d => miniAppWidth * d.data.applications / maxExampleLength)
        .attr("height", d => miniAppWidth * d.data.applications / maxExampleLength)
        .attr("rx", d => miniAppWidth * d.data.applications / (maxExampleLength * 2))
        .attr("ry", d => miniAppWidth * d.data.applications / (maxExampleLength * 2));

      group.selectAll("text")
        .style("opacity", 0);

      group.style("opacity", null);

    } else if (step === this.props.stepsEnum.SHOW_ALL_CIRCLES) {

      const group = g.selectAll(".mini-application")
        .transition().duration(1000)
        .attr("transform", d => "translate(" + d.packX + "," + d.packY + ")")
        .on("end", function() {
          d3.select(this).style("opacity", 0);
        });

      group.select("rect")
        .attr("stroke", "#fff")
        .attr("fill", "var(--base-data-color)")
        .attr("width", d => d.r * 2)
        .attr("height", d => d.r * 2)
        .attr("rx", d => d.r)
        .attr("ry", d => d.r);

      group.selectAll("text")
        .style("opacity", 0);
    }
  }

  showClusterLabels = () => {

    const data = this.state.data.filter(d => this.props.browsableIDs.has(d.data.id));

    const schoolGroups = d3.nest()
      .key(d => d.data.schools[0])
      .entries(data)
      .filter(d => clusterLabels[d.key] ? true : false);

    schoolGroups.forEach(function (group) {
      const points = group.values.map(function (d) {
        return {
          x: d.tsneX,
          y: d.tsneY
        };
      });
      let centroid = {};
      centroid.x = d3.mean(points, function (d) { return d.x; });
      centroid.y = d3.mean(points, function (d) { return d.y; });
      points.forEach(function (point) {
        const xDiff = point.x - centroid.x;
        const yDiff = point.y - centroid.y;
        point.dist = Math.sqrt(xDiff * xDiff + yDiff * yDiff);
      });
      points.sort(function (a, b) { return a.dist - b.dist; });
      const closest = points.slice(0, Math.round(points.length * 0.75));
      centroid.x = d3.mean(closest, function (d) { return d.x; });
      centroid.y = d3.mean(closest, function (d) { return d.y; });
      group.centroid = centroid;
    });

    const schoolNames = d3.select(this.chartArea).selectAll('.school-name')
      .data(schoolGroups);

    const schoolNamesEnter = schoolNames
      .enter().append('g')
      .style('opacity', 0)
      .attr('class', 'school-name')
      .attr('transform', d => 'translate(' + d.centroid.x + ',' + d.centroid.y + ')');

    schoolNamesEnter.append('text')
      .text(d => this.props.schoolCodes.get(d.key).name)
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .style('stroke-width', 3)
      .style('stroke', 'white');

    schoolNamesEnter.append('text')
      .text(d => this.props.schoolCodes.get(d.key).name)
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em');

    schoolNames.merge(schoolNamesEnter).transition().delay(1000).duration(1000)
      .style('opacity', 0.75);

    // Update (in case of resize)
    schoolNames
      .attr('transform', d => 'translate(' + d.centroid.x + ',' + d.centroid.y + ')');

  }

  update() {

    if (this.state.data.length === 0) return;
    let data = [];

    if (this.props.step <= this.props.stepsEnum.SHOW_ALL_CIRCLES) {
      this.showIntro();
    }

    if (this.props.step === this.props.stepsEnum.SHOW_ALL_CIRCLES) {
      data = this.state.data; // TODO: add an intermediate step where data = examples?
    } else if (this.props.step >= this.props.stepsEnum.HIDE_SMALLEST_CIRCLES) {
      data = this.state.data.filter(d => this.props.browsableIDs.has(d.data.id));
    }

    //console.log(data);
    let xAccessor = d => d.packX;
    let yAccessor = d => d.packY;
    if (this.props.step >= this.props.stepsEnum.TRANSITION_TO_SIMILARITY_LAYOUT) {
      xAccessor = d => d.tsneX;
      yAccessor = d => d.tsneY;
    }

    const g = d3.select(this.chartArea);

    let circles = g.selectAll('circle')
      .data(data, d => d.data.id); // use application ID in key function

    circles.enter().append('circle')
      .attr('cx', xAccessor)
      .attr('cy', yAccessor)
      .attr('r', 0)
      .on('click', (d) => {
        this.props.handleClick(d);
      })
      .merge(circles)
      .transition()
      .duration(1000)
      .attr('cx', xAccessor)
      .attr('cy', yAccessor)
      .attr('r', d => d.r)  // use radius set by pack

    circles.exit()
      .transition()
      .duration(1000)
      .attr('r', 0)
      .remove();

    if (this.props.step >= this.props.stepsEnum.TRANSITION_TO_SIMILARITY_LAYOUT) {
      this.showClusterLabels();
    } else {
      d3.selectAll('.school-name').remove();
    }

    if (this.props.step >= this.props.stepsEnum.END_EXPLANATION) {
      g.selectAll('circle')
        .classed('highlighted', d => this.shouldHighlight(d.data))
        .classed('selected', d => d.data.id === this.props.clicked);
    } else {
      g.selectAll('circle')
        .classed('highlighted', false)
        .classed('selected', false);
    }
  }

  render() {
    return (
      <div className={this.props.step >= this.props.stepsEnum.END_EXPLANATION ? "visualization shifted" : "visualization"}>
        <svg width={this.props.drawWidth + margin.left + margin.right} height={this.props.drawHeight + margin.top + margin.bottom}>
          <g ref={(node) => { this.chartArea = node; }}
            transform={`translate(${margin.left}, ${margin.top})`} />
        </svg>
      </div>
    );
  }
}

export default ApplicationsVisualization;



// WEBPACK FOOTER //
// ./src/ApplicationsVisualization.js